/* eslint-disable @typescript-eslint/no-explicit-any */
import cloneDeep from "lodash-es/cloneDeep"

import { createResponseTransformer } from "shared/api/createResponseTransformer"
import { serializer } from "shared/api/serializer/serializer"
import { ProductConfigurationAPIType } from "shared/types/ProductConfiguration"
import { RJSFSchema } from "shared/types/RJSF"

import { flattenSchema } from "entities/dim-configuration/lib/flattenFormData"

const CURRENT_PRODUCT_ID = "root"
const ORIGINAL_PRODUCT_ID = "cap-without-dimensioning-influence"

function generateSchema(response: {
  properties?: RJSFSchema["properties"]
  product?: any
  addons?: any[]
  suffix?: string
}): RJSFSchema {
  const properties: RJSFSchema["properties"] = {}
  if (response?.properties) {
    const [key, property] = Object.entries(response.properties)[0]
    Object.assign(properties, {
      value: { fieldID: key, ...property }
    })
  }

  if (response?.product) {
    const flattenedSchema = flattenSchema({
      data: {
        ...response.product,
        properties: {
          ...response.product.properties
        }
      }
    })
    Object.assign(properties, {
      product: flattenedSchema
    })
  }

  if (response?.addons && response?.addons.length > 0) {
    Object.assign(properties, {
      addons: {
        type: "object",
        properties: Object.fromEntries(
          response.addons.map((addonSchema: RJSFSchema & { id: string }) => [
            response.suffix && addonSchema.id.includes(response.suffix)
              ? addonSchema.id.replace(`_${response.suffix}`, "")
              : addonSchema.id,
            generateSchema({ ...addonSchema, suffix: response.suffix })
          ])
        )
      }
    })
  }

  // if (response?.currentDimensioning) {
  //   Object.assign(properties, {
  //     dimensioning: response.currentDimensioning
  //   })
  // }

  // if (response?.meta && !isEmpty(response?.meta.properties)) {
  //   Object.assign(properties, {
  //     meta: response.meta
  //   })
  // }

  const result: RJSFSchema = {
    type: "object",
    properties
  }

  return result
}

export const productConfigurationResponseTransformer =
  createResponseTransformer(json => {
    const deserialized = serializer.deserialize(
      ProductConfigurationAPIType,
      json
    )
    const clone = cloneDeep(deserialized)

    const schema = generateSchema({
      product: clone.product,
      addons: clone.addons
        .filter((addon: any) => addon.id.includes(CURRENT_PRODUCT_ID))
        .map((addon: any) => ({
          ...addon,
          id: addon.id.replace(`_${CURRENT_PRODUCT_ID}`, "")
        })),
      suffix: CURRENT_PRODUCT_ID
    })

    delete clone.product
    delete clone.products
    delete clone.specialAttributes
    delete clone.addons
    delete clone.currentDimensioning
    delete clone.meta

    const originalProduct = clone.products?.find(
      (product: any) => product.id === ORIGINAL_PRODUCT_ID
    )
    let originalConfiguration

    if (originalProduct) {
      const originalAddons = clone.addons
        ?.filter((product: any) => product.id.includes(ORIGINAL_PRODUCT_ID))
        .map((addon: any) => ({
          ...addon,
          id: addon.id.replace(`_${ORIGINAL_PRODUCT_ID}`, "")
        }))

      const originalSchema = generateSchema({
        product: originalProduct,
        addons: originalAddons,
        suffix: ORIGINAL_PRODUCT_ID
      })

      originalConfiguration = {
        ...clone,
        id: deserialized.configurationKey,
        schema: originalSchema,
        dimensioningOptions: []
      }
    }

    return {
      ...clone,
      id: `${deserialized.configurationKey}${
        deserialized.configurationKey && deserialized.dimensioningKey ? "-" : ""
      }${deserialized.dimensioningKey ?? ""}`,
      schema,
      originalConfiguration,
      dimensioningOptions: []
    }
  })
